import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { FormField, Select, TextField } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import { Alert, Card, CardBody, CardFooter, FormActions, StatusStrip } from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { routes } from '~/app/routes';
import { queryErrorMessage } from '~/modules/query';
import {
  useGetDistinctHostsQuery,
  useGetServicesQuery,
  useRunQuickActionMutation,
} from '~/modules/query/services';
import {
  SERVICE_HEALTH,
  SERVICE_STATUS,
  serviceChip,
  type ServiceRow,
} from '~/components/Constants/serviceConstants';

type Kind = 'update' | 'move';

const COPY = {
  update: {
    verb: 'Update service',
    description: 'Set the image this service runs.',
    success: 'Service updated',
    failure: 'The service could not be updated. Try again.',
    schema: z.object({ value: z.string().trim().min(1, 'Enter the image URL.') }),
  },
  move: {
    verb: 'Move service',
    description: 'Move this service to another host.',
    success: 'Service moved',
    failure: 'The service could not be moved. Try again.',
    schema: z.object({ value: z.string().min(1, 'Choose the host to move the service to.') }),
  },
} as const;

const FIELD_ID = 'service-action-value';

/** Update (image) or Move (host) one service (DESIGN.md §1.6). */
function ServiceActionPage({ kind }: { kind: Kind }) {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const copy = COPY[kind];
  const list = useGetServicesQuery();
  const service = (list.data?.data as ServiceRow[] | undefined)?.find((row) => row._id === id);
  const hosts = useGetDistinctHostsQuery(undefined, { skip: kind !== 'move' || !service });
  const hostOptions = useMemo(
    () =>
      (hosts.data?.distinctHosts ?? [])
        .filter((host) => !service?.host || !host.includes(service.host))
        .map((host) => ({ label: host, value: host })),
    [hosts.data, service?.host],
  );

  // Update prefills the current image; Move defaults to the first other host.
  let seed: string | undefined;
  if (service && kind === 'update') seed = service.image || '';
  if (service && kind === 'move' && hosts.data) seed = hostOptions[0]?.value ?? '';
  const { draft, patch, dirty } = useRecordDraft(seed, {
    id: `${kind}-${id}`,
    toDraft: (value: string) => ({ value }),
  });
  const guard = useUnsavedGuard(dirty);
  const v = useFormValidation(copy.schema, draft, () => FIELD_ID);
  const [runQuickAction] = useRunQuickActionMutation();

  const save = useRecordSave<{ value: string }>({
    save: ({ value }) =>
      runQuickAction({
        action: kind,
        ids: id,
        ...(kind === 'move' ? { newHost: value } : { newImage: value }),
      }).unwrap(),
    success: copy.success,
    failure: copy.failure,
  });

  const title = service?.name ?? 'Service';
  const loadError = !service ? list.error : hosts.error;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !v.submit()) return;
    if (await save.submit(draft)) {
      guard.release();
      navigate(routes.services);
    }
  };

  return (
    <RecordPageShell
      title={title}
      description={copy.description}
      breadcrumbs={[{ label: 'Services', href: routes.services }, { label: title }]}
      loading={!draft}
      notFound={!service && !list.isLoading && !list.isError}
      notFoundTitle="Service not found"
      backTo={routes.services}
      backLabel="Back to services"
      error={loadError ? { what: 'the service', message: queryErrorMessage(loadError) } : null}
      onRetry={() => (service ? hosts.refetch() : list.refetch())}
      guard={guard}
      testId="service-action"
    >
      {service && (
        <StatusStrip
          chip={serviceChip(SERVICE_STATUS, service.status)}
          id={service._id}
          facts={[
            `Health: ${serviceChip(SERVICE_HEALTH, service.health).label}`,
            `Host: ${service.host || '—'}`,
          ]}
          jump={{ label: 'View services', href: routes.services }}
          testId="service-status"
        />
      )}
      {draft && (
        <Card>
          <CardBody className="space-y-4">
            {save.error && (
              <Alert variant="error">
                <p className="text-sm text-gray-800 dark:text-white/90">{save.error}</p>
              </Alert>
            )}
            <form id="service-action-form" noValidate onSubmit={submit}>
              {kind === 'move' ? (
                <FormField id={FIELD_ID} label="Move container to" error={v.errors.value}>
                  <Select
                    id={FIELD_ID}
                    testId="service-move-host"
                    ariaLabel="Move container to"
                    options={hostOptions}
                    value={draft.value}
                    onChange={(value) => patch({ value })}
                  />
                </FormField>
              ) : (
                <TextField
                  id={FIELD_ID}
                  testId="service-update-image"
                  label="Update image to"
                  placeholder="Image URL"
                  value={draft.value}
                  onChange={(value) => patch({ value })}
                  onBlur={() => v.touch('value')}
                  error={v.errors.value}
                  code
                />
              )}
            </form>
          </CardBody>
          <CardFooter>
            <FormActions
              form="service-action-form"
              discardHref={routes.services}
              discardLabel="Cancel"
              saveLabel={copy.verb}
              busyLabel={`${copy.verb.split(' ')[0]}…`}
              busy={save.busy}
              saveTestId="service-action-submit"
            />
          </CardFooter>
        </Card>
      )}
    </RecordPageShell>
  );
}

export const ServiceUpdatePage = () => <ServiceActionPage kind="update" />;
export const ServiceMovePage = () => <ServiceActionPage kind="move" />;
